import React from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Module, Section, Activity } from "../../schemas/schemas";

interface CourseNavigationProps {
  modules: Module[];
  t: (key: string) => string;
}

export function CourseNavigation({ modules, t }: CourseNavigationProps) {
  return (
    <div className="md:w-1/4 flex-shrink-0">
      <Card className="sticky top-36">
        <CardHeader>
          <CardTitle>{t('course-content.composer.contentCourse')}</CardTitle>
          <CardDescription>{t('course-content.composer.navigateContent')}</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="space-y-4">
            {modules.map((module: Module) => (
              <div
                key={module.id}
                className="border border-gray-200 dark:border-gray-800"
              >
                <div className="flex items-center justify-between bg-gray-50 px-4 py-3 dark:bg-gray-800">
                  <div className="font-medium">{module.name}</div>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    {module.type}
                  </span>
                </div>
                <div className="divide-y divide-gray-200 dark:divide-gray-800">
                  {(module.sections || []).map((section: Section) => (
                    <div
                      key={section.id}
                      className={`flex items-center justify-between px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer`}
                    >
                      <div className="flex items-center">
                        <span className="mr-2 text-gray-500">
                          {section.activities && section.activities.length > 0 ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M21 15V10a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2z"></path>
                              <path d="M17 10h4"></path>
                              <path d="M19 13h2"></path>
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M12 20h9"></path>
                              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                            </svg>
                          )}
                        </span>
                        <div className="text-sm font-medium">
                          {section.name}
                        </div>
                        <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                          {section.delivery_method}
                        </span>
                      </div>
                      <div>
                        <span
                          className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                            section.activities && section.activities.length > 0
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                          }`}
                        >
                          {section.activities && section.activities.length > 0 
                            ? `${section.activities.length} activities` 
                            : 'Empty'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="p-4">
            <Button variant="outline" size="sm" className="w-full">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="mr-2"
              >
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              {t('course-content.composer.addContent')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 